import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import transactionService from '../../../services/transactionService';

const TradeForm = ({ selectedCrypto, onTradeExecute }) => {
  const [tradeType, setTradeType] = useState('buy');
  const [orderType, setOrderType] = useState('market');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');
  const [total, setTotal] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  const availableBalance = 50000;
  const availableCrypto = 1.2345;

  const orderTypeOptions = [
    { value: 'market', label: 'Market Order', description: 'Execute immediately at current market price' },
    { value: 'limit', label: 'Limit Order', description: 'Execute only at your specified price or better' },
    { value: 'stop', label: 'Stop Order', description: 'Trigger market order when price reaches stop price' }
  ];

  const calculateTotal = (amt, prc) => {
    const amountNum = parseFloat(amt) || 0;
    const priceNum = parseFloat(prc) || selectedCrypto?.price;
    return (amountNum * priceNum)?.toFixed(2);
  };

  const handleAmountChange = (value) => {
    setAmount(value);
    setTotal(calculateTotal(value, price || selectedCrypto?.price));
  };

  const handlePriceChange = (value) => {
    setPrice(value);
    setTotal(calculateTotal(amount, value));
  };

  const handleTotalChange = (value) => {
    setTotal(value);
    const priceNum = parseFloat(price) || selectedCrypto?.price;
    if (priceNum > 0) {
      setAmount((parseFloat(value) / priceNum)?.toFixed(8));
    }
  };

  const handlePercentageClick = (percentage) => {
    if (tradeType === 'buy') {
      const totalAmount = (availableBalance * percentage) / 100;
      setTotal(totalAmount?.toFixed(2));
      const priceNum = parseFloat(price) || selectedCrypto?.price;
      setAmount((totalAmount / priceNum)?.toFixed(8));
    } else {
      const cryptoAmount = (availableCrypto * percentage) / 100;
      setAmount(cryptoAmount?.toFixed(8));
      setTotal(calculateTotal(cryptoAmount, price || selectedCrypto?.price));
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setShowConfirmation(true);
  };

  const handleConfirmTrade = () => {
    const tradeData = {
      type: tradeType,
      orderType,
      amount: parseFloat(amount),
      price: parseFloat(price) || selectedCrypto?.price,
      total: parseFloat(total),
      crypto: selectedCrypto
    };
    
    onTradeExecute(tradeData);
    
    // Create transaction in database and send email notification
    const userId = localStorage.getItem('userId'); // Get from auth context in production
    if (userId) {
      transactionService?.createTransaction(userId, {
        type: tradeType,
        cryptoSymbol: selectedCrypto?.symbol,
        cryptoAmount: parseFloat(amount),
        usdAmount: parseFloat(total),
        pricePerUnit: parseFloat(price) || selectedCrypto?.price,
        description: `${tradeType === 'buy' ? 'Bought' : 'Sold'} ${selectedCrypto?.name}`,
        feeAmount: parseFloat(total) * 0.001,
        status: 'completed'
      });
    }
    
    setShowConfirmation(false);
    setAmount('');
    setPrice('');
    setTotal('');
  };

  return (
    <>
      <div className="bg-card rounded-lg border border-border h-full flex flex-col">
        <div className="flex items-center border-b border-border">
          <button
            onClick={() => setTradeType('buy')}
            className={`flex-1 py-3 md:py-4 text-sm md:text-base font-semibold transition-colors ${
              tradeType === 'buy' ?'text-success border-b-2 border-success' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Buy {selectedCrypto?.symbol}
          </button>
          <button
            onClick={() => setTradeType('sell')}
            className={`flex-1 py-3 md:py-4 text-sm md:text-base font-semibold transition-colors ${
              tradeType === 'sell' ?'text-error border-b-2 border-error' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Sell {selectedCrypto?.symbol}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col p-4 md:p-6">
          <div className="space-y-4 flex-1">
            <Select
              label="Order Type"
              options={orderTypeOptions}
              value={orderType}
              onChange={setOrderType}
            />

            <div className="bg-muted/50 rounded-lg p-3 md:p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs md:text-sm text-muted-foreground">Available Balance</span>
                <span className="text-sm md:text-base font-semibold text-foreground">
                  {tradeType === 'buy' 
                    ? `$${availableBalance?.toLocaleString()}` 
                    : `${availableCrypto} ${selectedCrypto?.symbol}`}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Icon name="Info" size={14} />
                <span>
                  {tradeType === 'buy' ?'Amount available for purchasing cryptocurrency' :'Amount of cryptocurrency available for selling'}
                </span>
              </div>
            </div>

            {orderType !== 'market' && (
              <Input
                label="Price"
                type="number"
                placeholder="Enter price"
                value={price}
                onChange={(e) => handlePriceChange(e?.target?.value)}
                description={`Current market price: $${selectedCrypto?.price?.toLocaleString()}`}
              />
            )}

            <Input
              label={`Amount (${selectedCrypto?.symbol})`}
              type="number"
              placeholder="0.00000000"
              value={amount}
              onChange={(e) => handleAmountChange(e?.target?.value)}
              required
            />

            <div className="flex items-center gap-2">
              {[25, 50, 75, 100]?.map((percentage) => (
                <button
                  key={percentage}
                  type="button"
                  onClick={() => handlePercentageClick(percentage)}
                  className="flex-1 py-2 text-xs md:text-sm font-medium text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/70 rounded-md transition-colors"
                >
                  {percentage}%
                </button>
              ))}
            </div>

            <Input
              label="Total (USD)"
              type="number"
              placeholder="0.00"
              value={total}
              onChange={(e) => handleTotalChange(e?.target?.value)}
              required
            />

            <div className="bg-muted/30 rounded-lg p-3 md:p-4 space-y-2">
              <div className="flex items-center justify-between text-xs md:text-sm">
                <span className="text-muted-foreground">Estimated Fee</span>
                <span className="font-medium text-foreground">
                  ${(parseFloat(total) * 0.001)?.toFixed(2)} (0.1%)
                </span>
              </div>
              <div className="flex items-center justify-between text-xs md:text-sm">
                <span className="text-muted-foreground">You will {tradeType === 'buy' ? 'receive' : 'get'}</span>
                <span className="font-semibold text-foreground">
                  {tradeType === 'buy' 
                    ? `${amount || '0.00000000'} ${selectedCrypto?.symbol}`
                    : `$${(parseFloat(total) * 0.999)?.toFixed(2)}`}
                </span>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            variant={tradeType === 'buy' ? 'success' : 'danger'}
            fullWidth
            size="lg"
            disabled={!amount || !total}
            className="mt-4"
          >
            {tradeType === 'buy' ? 'Buy' : 'Sell'} {selectedCrypto?.symbol}
          </Button>
        </form>
      </div>
      {showConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-card rounded-lg border border-border shadow-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg md:text-xl font-semibold text-foreground">Confirm Trade</h3>
              <button
                onClick={() => setShowConfirmation(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-muted-foreground">Order Type</span>
                  <span className="text-sm font-medium text-foreground capitalize">{orderType}</span>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-muted-foreground">Action</span>
                  <span className={`text-sm font-semibold ${tradeType === 'buy' ? 'text-success' : 'text-error'}`}>
                    {tradeType?.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-muted-foreground">Amount</span>
                  <span className="text-sm font-medium text-foreground">
                    {amount} {selectedCrypto?.symbol}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-muted-foreground">Price</span>
                  <span className="text-sm font-medium text-foreground">
                    ${(parseFloat(price) || selectedCrypto?.price)?.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <span className="text-base font-semibold text-foreground">Total</span>
                  <span className="text-base font-bold text-foreground">${parseFloat(total)?.toLocaleString()}</span>
                </div>
              </div>

              <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 rounded-lg p-3">
                <Icon name="AlertCircle" size={16} className="flex-shrink-0 mt-0.5" />
                <p>
                  Please review your order carefully. Once confirmed, this transaction cannot be reversed. 
                  Market conditions may cause slight price variations during execution.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowConfirmation(false)}
              >
                Cancel
              </Button>
              <Button
                variant={tradeType === 'buy' ? 'success' : 'danger'}
                fullWidth
                onClick={handleConfirmTrade}
              >
                Confirm {tradeType === 'buy' ? 'Buy' : 'Sell'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TradeForm;